// Single mapping point between EditorDoc and the FastAPI wire shape.
// All wire positions are metres in the Y-up admin frame.

import type {
  AdminSpeaker,
  AdminPanel,
  AdminFurniture,
  AdminWindow,
  MaterialAssignmentWire,
  Vec3,
  RaytraceRequest,
  SpeakerSweepRequest,
  Scene,
  WingSpec,
  EditorDoc,
  SpeakerEntry,
  PanelEntry,
  FurnitureEntry,
  WindowEntry,
  PanelMounting,
  RoomTreatmentResponse,
  PanelAbsorptionRequest,
} from '@roomtreatment/core';
import {
  panelFootprint,
  interiorAngleForPanel,
  syncWindowGeometry,
  windowPolygon,
  mediaDeviceSize,
  mediaPictureSize,
  WINDOW_GLASS_MATERIAL,
  type MediaEntry,
} from '@roomtreatment/core';
import {
  roomSurfaces, parseSurfaceKey, sameSurface, poseToTransform,
  type SurfaceFrame,
} from '@roomtreatment/panel-surface';
import { furnitureAcoustic } from './furnitureAcoustics';

function genId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `id-${Math.random().toString(36).slice(2)}`;
}

export function speakerToWire(s: SpeakerEntry): AdminSpeaker {
  return {
    position: s.position,
    subkind: s.subkind,
    role: s.role,
    freq_response: s.freqResponse,
  };
}

export function panelToWire(p: PanelEntry, interiorAngle?: number): AdminPanel {
  return {
    label: p.label,
    sku: p.sku,
    subkind: p.subkind,
    size: p.size,
    position: p.position,
    rotation: p.rotation,
    // `shape` is the orthogonal "is this a corner trap" axis — the wire's
    // `mounting_type` is where the backend reads corner-ness from, so it must
    // win over `p.mounting` whenever the panel is a corner trap.
    mounting_type: p.shape === 'corner' ? 'corner' : p.mounting,
    // The panel's real plan-view outline, so the backend extrudes the actual
    // shape instead of a bounding box. Null for a plain box — which is most
    // panels — so their payload stays exactly what it has always been and
    // the solver keeps taking its existing box path.
    footprint: panelFootprint(p, interiorAngle),
    airgap_m: p.airGap ?? null,
    sigma: p.sigma ?? null,
  };
}

export function furnitureToWire(f: FurnitureEntry): AdminFurniture {
  const preset = furnitureAcoustic(f.presetId);
  const kind = preset?.kind ?? 'solid';
  return {
    preset_id: f.presetId,
    size: f.size,
    position: f.position,
    rotation: f.rotation,
    // Decor carries no material: the solver skips it on `kind` alone, so the
    // 'upholstered' fallback would put a real α curve on a field nothing may
    // read. The item still goes over the wire — deciding what is acoustically
    // relevant is the physics layer's call, not this one's.
    material: f.material ?? preset?.absorptionMaterial ?? (kind === 'decor' ? null : 'upholstered'),
    kind,
    // Client decides what counts as a seat (preset category === 'Seating');
    // the physics layer decides what to do with one (seat-anchored search,
    // §4.5 of the unified-solve plan). An unknown presetId defaults to
    // false, same as every other fallback above.
    seat: preset?.seat ?? false,
  };
}

/** A TV, a projection screen and a projector casing are all hard, reflective
 *  boxes as far as the solver is concerned — glass, plastic and painted metal
 *  sit on the same curve (materials.py's `hard_reflective`, α ≈ 0.03). */
const MEDIA_MATERIAL = 'hard_reflective';

/** Local +Z of an XYZ Euler, without pulling a matrix library in here. This is
 *  the room-facing normal for anything placed by `poseToTransform`, and it is
 *  what a wall TV's mount standoff pushes it along. */
function localZ(rot: Vec3): Vec3 {
  const [rx, ry] = rot;
  // R = Rx·Ry·Rz applied to (0,0,1); Rz leaves +Z alone, so only rx/ry matter.
  return [
    Math.sin(ry) * Math.cos(rx),
    -Math.sin(rx),
    Math.cos(ry) * Math.cos(rx),
  ];
}

/**
 * Media as the solver sees it: oriented boxes on the existing furniture wire.
 *
 * Nothing about `AdminFurniture` assumes the floor — it is a free OBB with a
 * material — so a wall-hung TV needs no new wire shape and no backend change.
 * What it buys, for free: `furniture_obb_facets` gives it ray facets (so
 * reflections off a TV show up in the raytrace), and `_mark_furniture` blocks
 * the wall and ceiling cells it intrudes, so the solver will not place a panel
 * on top of it.
 *
 * One entry can produce two boxes — a projector is a casing AND the screen it
 * throws at — or one, or none of the screen at all. See the individual cases.
 */
export function mediaToWire(m: MediaEntry, surfaces: SurfaceFrame[]): AdminFurniture[] {
  const out: AdminFurniture[] = [];
  const device = mediaDeviceSize(m.kind, m);

  // A TV's stored transform is its mounting ANCHOR (PanelObject's convention,
  // which media follows), and `standoff` holds the body off the wall along its
  // own normal. `AdminFurniture` has no airgap field the way `AdminPanel`
  // does, so that offset is applied HERE rather than by the backend — leaving
  // it out would sink every wall TV into its wall by up to 10 cm.
  const standoff = m.kind === 'tv' ? (m.standoff ?? 0) : 0;
  const n = localZ(m.rotation);
  out.push({
    preset_id: `media:${m.kind}`,
    size: device,
    position: [
      m.position[0] + n[0] * standoff,
      m.position[1] + n[1] * standoff,
      m.position[2] + n[2] * standoff,
    ],
    rotation: m.rotation,
    material: MEDIA_MATERIAL,
    kind: 'solid',
    seat: false,
  });

  const screen = m.kind === 'projector' ? m.screen : undefined;
  if (screen && !screen.acousticallyTransparent) {
    const ref = parseSurfaceKey(screen.surface);
    const frame = ref ? surfaces.find((f) => sameSurface(f.ref, ref)) : undefined;
    if (frame) {
      const size = mediaPictureSize('projector', m);
      const t = poseToTransform(frame, { u: screen.u, v: screen.v, spin: 0 }, size);
      out.push({
        preset_id: 'media:screen',
        size,
        position: t.position,
        rotation: t.rotation,
        material: MEDIA_MATERIAL,
        kind: 'solid',
        seat: false,
      });
    }
  }
  // An acoustically transparent screen emits nothing at all. A woven screen is
  // close to acoustically invisible across the band this solver works in, and
  // the wire has no way to say "geometry sound passes through" — so the honest
  // first approximation is to leave it out rather than model it as a
  // reflector. See the media plan's 6.2 for what a fuller model would need.

  return out;
}

/** Every media box in the document, ready to append to `furniture`. */
export function mediaBoxes(doc: EditorDoc): AdminFurniture[] {
  const media = doc.media ?? [];
  if (media.length === 0) return [];
  const surfaces = roomSurfaces(doc);
  return media.flatMap((m) => mediaToWire(m, surfaces));
}

export function windowToWire(w: WindowEntry): AdminWindow {
  const synced = syncWindowGeometry(w);
  // frameWidth/frameMaterial are visual-only (3D trim) and intentionally
  // excluded — the solver stamps the whole opening as glass.
  return {
    surface: synced.surface,
    polygon: windowPolygon(synced).map((p) => [p.u, p.v]),
    u: synced.u,
    v: synced.v,
    w: synced.w,
    h: synced.h,
    thickness: synced.thickness,
    material: synced.material ?? WINDOW_GLASS_MATERIAL,
  };
}

/** from_legacy rectangular labels: edge 0 (y=0) is left. */
const BOX_WALL_LABELS = ['left', 'back', 'right', 'front'] as const;

function surfaceKeyToAssignment(
  key: string,
  materialId: string,
  doc: EditorDoc,
): MaterialAssignmentWire {
  if (key === 'floor') return { target_kind: 'floor', target_id: 'floor', material_id: materialId };
  // Every ceiling facet resolves to the one ceiling target. The solver's voxel
  // model has exactly six face groups and reads all ceiling absorption from
  // `target_id: 'ceiling'` (backend/material_resolver.py), so per-facet ceiling
  // ACOUSTICS does not exist — only per-facet colour does. Matching the facet
  // form here is what stops a `ceiling:2` key falling through to the wall
  // branch below and silently assigning a roof material to a wall.
  if (/^ceiling(:\d+)?$/.test(key)) {
    return { target_kind: 'ceiling', target_id: 'ceiling', material_id: materialId };
  }
  const m = /^wall:(\d+)$/.exec(key);
  if (m) {
    const idx = Number(m[1]);
    const labelled = doc.footprint?.every((c) => c.label)
      ? doc.footprint.map((c) => c.label as string)
      : null;
    const target_id = labelled?.[idx]
      ?? (doc.footprint == null || doc.footprint.length === 4
        ? BOX_WALL_LABELS[idx] ?? `wall_${idx}`
        : `wall_${idx}`);
    return { target_kind: 'wall', target_id, material_id: materialId };
  }
  return { target_kind: 'wall', target_id: key, material_id: materialId };
}

export function materialsToWire(doc: EditorDoc): Scene['materials'] {
  const assignments: MaterialAssignmentWire[] = [];
  for (const [key, mid] of Object.entries(doc.surfaceMaterials ?? {})) {
    if (mid) assignments.push(surfaceKeyToAssignment(key, mid, doc));
  }
  for (const f of doc.furniture) {
    if (f.material) {
      assignments.push({
        target_kind: 'furniture',
        target_id: f.id,
        material_id: f.material,
      });
    }
  }
  if (assignments.length === 0) return undefined;
  return {
    assignments,
    defaults: {
      wall: doc.wallMaterial ?? undefined,
      floor: doc.floorMaterial ?? undefined,
    },
  };
}

function listenerPos(doc: EditorDoc): Vec3 {
  return doc.listener
    ? doc.listener.position
    : [doc.dims.Lx / 2, 1.2, doc.dims.Ly / 2];
}

/** Room + sources + panels in the shared SceneBase request shape. */
export function docToScenePayload(doc: EditorDoc) {
  return {
    Lx: doc.dims.Lx,
    Ly: doc.dims.Ly,
    H: doc.dims.H,
    speakers: doc.speakers.map(speakerToWire),
    listener_pos: listenerPos(doc),
    panels: doc.panels.map((p) => panelToWire(p, interiorAngleForPanel(doc, p))),
    furniture: [...doc.furniture.map(furnitureToWire), ...mediaBoxes(doc)],
    floor_material: doc.floorMaterial,
    wall_material: doc.wallMaterial,
    ...geometryToWire(doc),
  };
}

/** A solver-returned panel mapped into a document entry with a fresh id. */
export function panelFromWire(p: AdminPanel): PanelEntry {
  const isCorner = p.mounting_type === 'corner';
  const mounting: PanelMounting =
    p.mounting_type === 'wall' || p.mounting_type === 'ceiling' || p.mounting_type === 'floor'
      ? p.mounting_type
      : 'wall';
  return {
    id: genId(),
    sku: p.sku ?? null,
    label: p.label ?? null,
    subkind: p.subkind ?? null,
    size: p.size,
    mounting,
    position: p.position,
    rotation: p.rotation,
    airGap: p.airgap_m ?? undefined,
    sigma: p.sigma ?? undefined,
    shape: isCorner ? 'corner' : undefined,
  };
}

/** Replace doc panels with a `place_treatment` response. */
export function applyTreatmentResponse(
  doc: EditorDoc,
  res: RoomTreatmentResponse,
): EditorDoc {
  return { ...doc, panels: res.panels.map(panelFromWire) };
}

function geometryToWire(doc: EditorDoc): Partial<Scene> {
  const fp = doc.footprint && doc.footprint.length >= 3 ? doc.footprint : null;
  const wings: WingSpec[] = doc.wings.map((w) => ({
    wall: w.wall, depth: w.depth, width: w.width, offset: w.offset,
  }));
  return {
    room_shape: doc.roomShape,
    footprint_vertices: fp ? fp.map((c) => [c.x, c.y]) : null,
    wall_labels:
      fp && fp.every((c) => c.label) ? fp.map((c) => c.label ?? null) : null,
    wings,
    ceiling_profile: doc.ceiling,
    // Doors are not a distinct wire type — the backend duck-types on
    // {surface, u, v, w, h, polygon, thickness, material} and never
    // distinguishes a window from a door (see backend/window_stamp.py's
    // module docstring). Merging here is the ONE place that wires doors into
    // every physics request docToScene/docToRaytraceRequest/etc. spread this
    // from — zero backend changes needed beyond the material catalog.
    windows: [...(doc.windows ?? []), ...(doc.doors ?? [])].map(windowToWire),
    ...(() => {
      const materials = materialsToWire(doc);
      return materials ? { materials } : {};
    })(),
  };
}

/** Full explicit room scene — unified shape for every /api/admin/jobs action. */
export function docToScene(doc: EditorDoc): Scene {
  return {
    Lx: doc.dims.Lx,
    Ly: doc.dims.Ly,
    H: doc.dims.H,
    speakers: doc.speakers.map(speakerToWire),
    listener_pos: listenerPos(doc),
    panels: doc.panels.map((p) => panelToWire(p, interiorAngleForPanel(doc, p))),
    furniture: [...doc.furniture.map(furnitureToWire), ...mediaBoxes(doc)],
    wall_material: doc.wallMaterial,
    floor_material: doc.floorMaterial,
    furnishing: doc.furnishing,
    ...geometryToWire(doc),
  };
}

/** Raytrace request shape — `opts` overrides any default field. */
export function docToRaytraceRequest(
  doc: EditorDoc,
  opts?: Partial<RaytraceRequest>,
): RaytraceRequest {
  return {
    Lx: doc.dims.Lx,
    Ly: doc.dims.Ly,
    H: doc.dims.H,
    speakers: doc.speakers.map(speakerToWire),
    listener_pos: listenerPos(doc),
    n_rays: 6000,
    max_bounces: 6,
    alpha: 0.1,
    panels: doc.panels.map((p) => panelToWire(p, interiorAngleForPanel(doc, p))),
    furniture: [...doc.furniture.map(furnitureToWire), ...mediaBoxes(doc)],
    floor_material: doc.floorMaterial,
    wall_material: doc.wallMaterial,
    ...geometryToWire(doc),
    ...opts,
  };
}

/** Per-panel simulation-absorption request — same geometry/panels payload as
 *  raytrace, keyed differently server-side (modal solve, not raycast). */
export function docToPanelAbsorptionRequest(
  doc: EditorDoc,
  opts?: Partial<PanelAbsorptionRequest>,
): PanelAbsorptionRequest {
  return {
    Lx: doc.dims.Lx,
    Ly: doc.dims.Ly,
    H: doc.dims.H,
    speakers: doc.speakers.map(speakerToWire),
    listener_pos: listenerPos(doc),
    panels: doc.panels.map((p) => panelToWire(p, interiorAngleForPanel(doc, p))),
    furniture: [...doc.furniture.map(furnitureToWire), ...mediaBoxes(doc)],
    floor_material: doc.floorMaterial,
    wall_material: doc.wallMaterial,
    ...geometryToWire(doc),
    ...opts,
  };
}

/** Speaker-position sweep request (Sweet Spot Map Mode B) — fixed listener,
 *  candidate SPEAKER positions swept over the whole floor by default.
 *  `opts` overrides any default field (e.g. a tighter `sweep_axis_bounds`). */
export function docToSpeakerSweepRequest(
  doc: EditorDoc,
  opts?: Partial<SpeakerSweepRequest>,
): SpeakerSweepRequest {
  return {
    Lx: doc.dims.Lx,
    Ly: doc.dims.Ly,
    H: doc.dims.H,
    listener_pos: listenerPos(doc),
    fixed_speaker_positions: doc.speakers.map((s) => s.position),
    sweep_axis_bounds: {
      x_min: 0, x_max: doc.dims.Lx,
      z_min: 0, z_max: doc.dims.Ly,
      grid_n: 12,
    },
    freq_lo_hz: 20,
    freq_hi_hz: 200,
    ...geometryToWire(doc),
    furniture: [...doc.furniture.map(furnitureToWire), ...mediaBoxes(doc)],
    ...opts,
  };
}
